import React, { useEffect, useState } from 'react';
import { Button, Input, Modal } from '../ui';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { getErrorDetail } from '../../utils/errorUtils';
import {
  PROJECT_ACCOUNTS_LOAD_FAILED,
  PROJECT_CREATE_FAILED,
  PROJECT_ENTITY_ID_REQUIRED,
} from '../../constants/error-messages';
import { ClientEntityRow } from '../../types/accounts';

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create so the board can refetch. */
  onCreated: () => void;
}

const CUSTOM_ENTITY = '__custom__';

/** Create a GTM project (name + optional client entity + optional Slack channel).
 *  The accounts dropdown only lists entities with prior activity, so a free-text
 *  UUID escape hatch covers the brand-new-client case. */
export const NewProjectModal: React.FC<NewProjectModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { showToast } = useToast();
  const [accounts, setAccounts] = useState<ClientEntityRow[]>([]);
  const [name, setName] = useState('');
  const [entityChoice, setEntityChoice] = useState('');
  const [customEntityId, setCustomEntityId] = useState('');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setEntityChoice('');
    setCustomEntityId('');
    setSlackChannelId('');
    // Customers-gated list — the canonical "our clients", incl. brand-new ones
    // with no stats yet (the accounts list structurally misses those).
    apiClient.getClientEntities().then(setAccounts).catch((err) => {
      // Non-fatal: the UUID escape hatch still works without the list — but an empty
      // dropdown otherwise reads as "this workspace has no clients", not as a failure.
      console.error('Failed to load accounts for project creation:', err);
      showToast(PROJECT_ACCOUNTS_LOAD_FAILED, 'error');
    });
  }, [isOpen, showToast]);

  const trimmedName = name.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmedName || submitting) return;
    const clientEntityId =
      entityChoice === CUSTOM_ENTITY ? customEntityId.trim() : entityChoice;
    // "Enter entity ID…" with nothing typed used to fall through to null and create a
    // clientless project — the operator's intent silently dropped, and the project only
    // discoverable as wrong later. Make them choose.
    if (entityChoice === CUSTOM_ENTITY && !clientEntityId) {
      showToast(PROJECT_ENTITY_ID_REQUIRED, 'error');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.createProject({
        name: trimmedName,
        client_entity_id: clientEntityId || null,
        slack_channel_id: slackChannelId.trim() || null,
      });
      showToast('Project created', 'success');
      onCreated();
      onClose();
    } catch (error) {
      console.error('Project creation failed:', error);
      showToast(getErrorDetail(error, PROJECT_CREATE_FAILED), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New project" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="new-project-name" className="block text-13 font-medium text-neutral-700 mb-1">
            Name
          </label>
          <Input
            id="new-project-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={500}
            required
            autoFocus
            fullWidth
            aria-describedby="new-project-name-help"
            placeholder="Acme — Q3 founders motion"
          />
          <p id="new-project-name-help" className="text-xs text-neutral-500 mt-1">
            Format: &lt;Client&gt; — &lt;motion&gt;
          </p>
        </div>

        <div>
          <label
            htmlFor="new-project-entity"
            className="block text-13 font-medium text-neutral-700 mb-1"
          >
            Client entity <span className="text-neutral-400 font-normal">(optional)</span>
          </label>
          <select
            id="new-project-entity"
            value={entityChoice}
            onChange={(e) => setEntityChoice(e.target.value)}
            className="w-full h-10 px-3 text-sm rounded-xl border border-neutral-200 bg-neutral-50/50 text-neutral-900 focus:outline-none focus:bg-white focus:border-neutral-300 focus:ring-4 focus:ring-primary-900/5"
          >
            <option value="">No client</option>
            {accounts.map((a) => (
              <option key={a.entity_id} value={a.entity_id}>
                {a.display_name || a.entity_id}
              </option>
            ))}
            <option value={CUSTOM_ENTITY}>Enter entity ID…</option>
          </select>
          {entityChoice === CUSTOM_ENTITY && (
            <Input
              id="new-project-entity-id"
              value={customEntityId}
              onChange={(e) => setCustomEntityId(e.target.value)}
              fullWidth
              className="mt-2"
              aria-label="Client entity ID"
              placeholder="Entity UUID"
            />
          )}
        </div>

        <div>
          <label
            htmlFor="new-project-slack"
            className="block text-13 font-medium text-neutral-700 mb-1"
          >
            Slack channel ID <span className="text-neutral-400 font-normal">(optional)</span>
          </label>
          <Input
            id="new-project-slack"
            value={slackChannelId}
            onChange={(e) => setSlackChannelId(e.target.value)}
            fullWidth
            placeholder="C0123456789"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="md" loading={submitting} disabled={!trimmedName}>
            Create project
          </Button>
        </div>
      </form>
    </Modal>
  );
};
